import Cookies from 'js-cookie';
import { UaParser } from '@mymove/user-agent';
import { make } from 'vuex-pathify';
import { get, post } from '../../http';

/**
 * Get list of ids for impress/click tracking
 * IMPORTANT - DOES NOT CURRENTLY WORK WITH RAW OFFERS
 * @param {Object[]} modifiedOffers
 * @return {String[]}
 */
export const getInteractionIds = (modifiedOffers) => modifiedOffers.map(({
  adId, adGroupId, accountId, templateId,
}) => ({
  adId, adGroupId, accountId, templateId,
}));

export const normalizeAnonymousId = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const defaultState = {
  orderUuid: '',
  clientID: '',
  anonymousId: '',
  offers: [],
  email: '',
  offersOptIn: '',
  firstName: '',
  lastName: '',
  businessName: '',
  mobilePhone: '',
  newAddress: {},
  oldAddress: {},
};
const mutations = {
  COAUUID_HANDLER(state) {
    const coaUUID = Cookies.get('coa') || sessionStorage.getItem('coaUUID');
    // eslint-disable-next-line no-param-reassign
    // eslint-disable-next-line no-param-reassign
    state.orderUuid = coaUUID;
  },
  ...make.mutations(defaultState),
};

const getters = {
  ...make.getters(defaultState),
  tcpaLegalText() {
    // eslint-disable-next-line max-len, quotes
    return "By providing my mobile phone number below, I agree to receive a promotional text message from USPS© using an automated system. I understand that my consent to receive the text message in not required to by goods or services. Carrier charges may apply.";
  },
};

const actions = ({
  MGO_API,
  clientId,
}) => ({
  ...make.actions(defaultState),

  async handleCoaUUID(context, payload) {
    context.commit('COAUUID_HANDLER', payload);
  },
  /**
   * @param {Object} storeContext - vuex store context
   * @param {Object} payload - action payload
   * @param {Boolean} payload.informedDelivery - opted in to informed delivery
   * @param {String} payload.deviceType - 'DESKTOP', 'MOBILE'
   * @return {String} anonymous id
   */
  async identify({ state, commit }, { informedDelivery, deviceType }) {
    commit('COAUUID_HANDLER');
    const uaParser = new UaParser();
    const remappedUA = uaParser.getParsedUAString(window.navigator.userAgent);
    const params = {
      earlyOptIn: false,
      informedDelivery,
    };
    if (deviceType) { params.deviceType = deviceType; }
    if (remappedUA) { params.userAgent = remappedUA; }

    const response = await post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}/i`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
        params,
      });
    const anonymousId = normalizeAnonymousId(response);

    commit('SET_ANONYMOUS_ID', anonymousId);

    return anonymousId;
  },

  prefetch({ state }) {
    return post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}/prefetch`, {}, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  impress({ state }, ads) {
    // exit before API call if we have no ads
    if (Object.keys(ads).length === 0) return Promise.resolve();
    // eslint-disable-next-line no-console
    // console.log('2--->', state);
    return post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}/t`, {
      eventType: 'ad-impressed',
      ads,
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  select({ state }, ads) {
    // exit before API call if we have no ads
    if (Object.keys(ads).length === 0) return Promise.resolve();
    // eslint-disable-next-line no-console
    // console.log('1--->', state);
    // track selections
    return post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}/t`, {
      eventType: 'ad-clicked',
      ads,
    }, {
      headers: {
        Authorization: `Bearer ${state.token}`,
      },
    });
  },

  async getOffers({ state, commit }, {
    schemas = [],
    viewAt = '',
  }) {
    const response = await post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}?schemas=${schemas.join(',')}&viewAt=${viewAt}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
    const ads = Array.isArray(response?.ads) ? response.ads : [];

    commit('SET_OFFERS', ads);

    return ads;
  },

  getTextOffers({ state, getters: localGetters }, payload) {
    const { phone, message } = payload;
    return post(`${MGO_API}/mgo-api/api/v1/sms/${state.orderUuid}/send`,
      {
        phone,
        message,
        tcpaLegalText: localGetters.tcpaLegalText,
      }, {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
  },

  async getReturnToSiteOffers({ state, commit }) {
    const schemas = [
      'MGO Advantage Mobile Full Page Expanded',
      'MGO Advantage Mobile Full Page Default',
    ];
    const { adIds } = await get(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}/selected`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
    const { ads } = await post(`${MGO_API}/mgo-offers/api/v1/o/${state.orderUuid}?schemas=${schemas.join(',')}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
    const selections = ads.filter(({ id }) => adIds.indexOf(id) >= 0);

    commit('SET_OFFERS', selections || []);

    return selections || [];
  },

  // SMS return to site texted link contains an encoded orderUuid
  // that needs to be decoded in order to retrieve offers
  // ex. https://mgodev01.imagitas.com/r/dvd2rqrgqha1uaxce1ni8x790
  async decodeOrderUuid({ state, commit }, encodedOrderUuid) {
    const { uuid: orderUuid } = await get(`${MGO_API}/mgo-api/api/v1/sms/${encodedOrderUuid}/decode`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });

    commit('SET_ORDER_UUID', orderUuid || '');

    return orderUuid || '';
  },
});

export default ({
  MGO_API,
  clientId,
}) => ({
  namespaced: true,
  state: defaultState,
  mutations,
  getters,
  actions: actions({
    MGO_API,
    clientId,
  }),
});
